import React, { Component } from 'react';
import _ from 'lodash';
import {
  courierInfoWidnowContent,
  googleMarkerIcon,
  infoWindowContent,
  zoomMap,
} from './mapUtils';
import { middleOfTheWorld } from '../../types/orderTypes';
import { MapRouter } from './MapRouter';
import { InstructionType } from '../../types/bundleTypes';
import { hexToRgb, rgbToHex } from '@material-ui/core';
import { invertColor } from '../../pages/oldPlanner/utils/bundleDraft';
import { square } from './mapMarkers';
import './DirectionsMap.scss';

export interface DirectionsMarkerIcon {
  info?: any;
  type?: string;
  backgroundColor?: string;
  color?: string;
  label?: string;
  stroke?: boolean;
  fillColor?: string;
  duration?: number;
}

export interface DirectionsMapMarker {
  icon: DirectionsMarkerIcon;
  lat: number;
  lng: number;
}

export interface CourierMarker {
  title: string;
  lat: number;
  lng: number;
}

export interface ConfirmedMarker {
  title: string;
  lat: number;
  lng: number;
}

interface Props {
  markers: DirectionsMapMarker[];
  courierMarker?: CourierMarker;
  confirmedMarker?: ConfirmedMarker;
  height: string | number;
  width: string | number;
  duration?: (duration: number) => void;
  distance?: (distance: number) => void;
  animate: boolean;
  refreshZoomPosition?: boolean;
  groups?: string[];
  windowColors?: Map<string, string>;
}

interface State {
  highlightIndex: number;
}

export class DirectionsMap extends Component<Props, State> {
  private markers: google.maps.Marker[] = [];
  private courierMarker: google.maps.Marker;
  private confirmedMarker: google.maps.Marker;

  private mapContainer: Element;
  private map: google.maps.Map;
  private infoWindow: google.maps.InfoWindow;
  private mapRouter: MapRouter;

  private intervalID: ReturnType<typeof setInterval>;

  static defaultProps = {
    height: 500,
    width: '100%',
    duration: () => {
      /* Empty default for optional param */
    },
    distance: () => {
      /* Empty default for optional param */
    },
    animate: false,
    refreshZoomPosition: true,
  };

  constructor(props: Props) {
    super(props);
    this.state = { highlightIndex: 0 };
  }

  componentDidMount() {
    this.map = new google.maps.Map(this.mapContainer, {
      zoom: 11,
      clickableIcons: true,
      center: middleOfTheWorld,
      disableDefaultUI: true,
      mapTypeControl: false,
      zoomControl: true,
    });
    const mapLegend = document.getElementById('direction-map-legend');
    this.mapRouter = new MapRouter({
      map: this.map,
      distanceCalculated: this.props.distance,
      durationCalculated: this.props.duration,
    });

    this.infoWindow = new google.maps.InfoWindow();
    this.map.addListener('click', () => {
      this.infoWindow.close();
    });
    if (mapLegend) {
      this.map.controls[google.maps.ControlPosition.TOP_RIGHT].push(mapLegend);
      google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
        // wait until map is ready before showing type filter to avoid flashing effect
        setTimeout(() => {
          mapLegend.style.display = 'grid';
        }, 500);
      });
    }
    new google.maps.TrafficLayer().setMap(this.map);
    this.runAllMarkers();
    zoomMap(this.markers, this.map);
    if (this.props.courierMarker && !this.courierMarker) {
      this.addCourier(this.props.courierMarker);
    }
    if (this.props.confirmedMarker && !this.confirmedMarker) {
      this.addConfirmedMarker(this.props.confirmedMarker);
    }

    if (this.props.animate) {
      this.intervalID = setInterval(() => this.animateMarkers(), 500);
    }
  }

  componentWillUnmount() {
    clearInterval(this.intervalID);
  }

  runAllMarkers() {
    this.addMarkers(this.props.markers);
    this.mapRouter.drawRoute(this.props.markers);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.courierMarker) {
      if (!this.courierMarker) {
        this.addCourier(this.props.courierMarker);
      } else {
        // Assuming that updating the position is not costly
        this.courierMarker.setPosition(
          new google.maps.LatLng(
            this.props.courierMarker.lat,
            this.props.courierMarker.lng
          )
        );
      }
    }

    if (this.props.confirmedMarker && !this.confirmedMarker) {
      this.addConfirmedMarker(this.props.confirmedMarker);
    }

    if (!_.isEqual(this.props.markers, prevProps.markers)) {
      this.clearMarkers();
      this.runAllMarkers();
      if (this.props.refreshZoomPosition) {
        zoomMap(this.markers, this.map);
      }
    }

    if (prevProps.animate !== this.props.animate) {
      clearInterval(this.intervalID);
      if (this.props.animate) {
        this.intervalID = setInterval(() => this.animateMarkers(), 500);
      }
    }
  }

  animateMarkers = () => {
    const markers = this.props.markers;
    const l = markers.length;
    if (l < 4) {
      return;
    }
    const prevI = this.state.highlightIndex;
    const prevIsPickup =
      markers[prevI]?.icon.info?.type === InstructionType.Pickup;
    let i = (this.state.highlightIndex + 1) % l;
    while (
      !(
        i === prevI ||
        markers[i].icon.info?.type === InstructionType.Delivery ||
        (!prevIsPickup && markers[i].icon.info?.type === InstructionType.Pickup)
      )
    ) {
      i = (i + 1) % l;
    }
    this.setState(
      {
        highlightIndex: i,
      },
      () => {
        this.highlightNextMarker(this.props.markers);
      }
    );
  };

  highlightNextMarker = (markers: DirectionsMapMarker[]) => {
    this.markers.forEach((marker, i) => {
      if (i === this.state.highlightIndex) {
        marker.setIcon(
          googleMarkerIcon({
            ...markers[i].icon,
            backgroundColor: 'rgb(255, 165, 0)',
          })
        );
        marker.setZIndex(1000);
      } else {
        marker.setIcon(googleMarkerIcon(markers[i].icon));
        marker.setZIndex(undefined);
      }
    });
  };

  clearMarkers = () => {
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];
  };

  addMarkers = (markers: DirectionsMapMarker[]) => {
    markers.forEach(marker => {
      const markerInfo =
        marker.icon && marker.icon.info ? marker.icon.info : null;
      let hexFromRGBA: string | any[], properHexColor: string;
      let color = marker.icon.color;
      if (!color && markerInfo && markerInfo.type !== 'courier') {
        hexFromRGBA = rgbToHex(marker.icon.fillColor);
        properHexColor = hexFromRGBA.slice(0, hexFromRGBA.length - 2);
        color = hexToRgb(invertColor(properHexColor, true));
      }
      const mapsMarker = new window.google.maps.Marker({
        position: { lat: marker.lat, lng: marker.lng },
        map: this.map,
        icon: googleMarkerIcon({
          type: marker.icon.type,
          backgroundColor: marker.icon.backgroundColor || marker.icon.fillColor,
          color,
          label: marker.icon.label,
          stroke: marker.icon.stroke,
        }),
        visible: true,
      });
      if (markerInfo && this.infoWindow) {
        mapsMarker.addListener('click', () => {
          markerInfo.type === 'courier'
            ? this.infoWindow.setContent(courierInfoWidnowContent(markerInfo))
            : this.infoWindow.setContent(infoWindowContent(markerInfo));

          this.infoWindow.open(this.map, mapsMarker);
        });
      }

      this.markers.push(mapsMarker);
    });
  };

  private addCourier(courierMarker: CourierMarker) {
    const mapsMarker = new window.google.maps.Marker({
      position: { lat: courierMarker.lat, lng: courierMarker.lng },
      map: this.map,
      icon: googleMarkerIcon({ type: 'porterbuddy', label: 'C' }),
      title: courierMarker.title,
      visible: true,
      zIndex: 100,
    });
    this.courierMarker = mapsMarker;
    this.courierMarker.setMap(this.map);
  }

  private addConfirmedMarker(confirmedMarker: ConfirmedMarker) {
    const mapsMarker = new window.google.maps.Marker({
      position: { lat: confirmedMarker.lat, lng: confirmedMarker.lng },
      map: this.map,
      icon: googleMarkerIcon({ type: 'deliveryConfirmed', label: '' }),
      title: confirmedMarker.title,
      visible: true,
      zIndex: 100,
    });
    this.confirmedMarker = mapsMarker;
    this.confirmedMarker.setMap(this.map);
  }

  render() {
    const { groups, windowColors } = this.props;
    let uniqueGroups: JSX.Element[];
    if (groups && windowColors) {
      uniqueGroups = Array.from(new Set(groups))
        .sort()
        .map(group => (
          <div key={group} className="direction-map-legend-window">
            <img src={square(windowColors.get(group))} alt={group} />
            <span>{group}</span>
          </div>
        ));
    }
    const { width, height } = this.props;
    return (
      <>
        {uniqueGroups && (
          <div id="direction-map-legend" className="direction-map-legend">
            <div>Click to show info.</div>
            <div>{uniqueGroups}</div>
          </div>
        )}
        <div
          ref={node => {
            this.mapContainer = node;
          }}
          style={{ height, width }}
        />
      </>
    );
  }
}
